import * as React from 'react';
import * as _ from 'lodash';
import classnames from 'classnames';
import { connect } from 'react-redux';

import SideBar from '../common/sidebar';
import MainContainer from './MainContainer';
import WorkspaceRouter from './WorkspaceRouter';
import { FeedbackPopup } from '../common/Popups';

import { ILocation } from '../../interface/common.interface';
import { IApplicationState } from '../../reducers';
import { Project, PROJECT_STATUS, VIEW_MODE } from '@nexxt/common/types';

import PageTitle from '../common/PageTitle';
import LiveProjectPopup from '../common/Popups/LiveProjectPopup';
import { setEditEnabled } from '../../actions/project.actions';

import AuthContext from '../../contexts/AuthContext';
import {
    AUTH_CONFIG,
    HARDENED_AUTH_TENANT_IDS
} from '@nexxt/common/firebase/auth/config';
import { getTenantId } from '@nexxt/common/firebase/auth/userDetails';
import { MiniModal, MiniModalActions, MiniModalContent } from 'inca-ui';
import axios from 'axios';
import { useIdleTimer } from 'react-idle-timer';

import { PageNotSupportedOnMobile } from '../common/noMatchPage';
import {
    getValueFromLocalStorage,
    setValueToLocalStorage
} from '@nexxt/common/utils/storage';
import { getUserDetails } from '@nexxt/common/firebase/auth/userDetails';
import { loadSession } from '../../services/session.service';

interface WorkspaceProps {
    currentProject: Project;
    isNewProjectCreated: boolean;
    location: ILocation;
    viewMode: VIEW_MODE;
    history: any;
    setEditEnabled: (toggle: boolean) => void;
}

const Workspace: React.SFC<WorkspaceProps> = (props: WorkspaceProps) => {
    const user = getUserDetails();
    const [showFeedbackPopup, setShowFeedbackPopup] = React.useState(false);
    // const [showLiveProjectPopup, setShowLiveProjectPopup] =
    //     React.useState(false);
    const [isMobilePage, setIsMobilePage] = React.useState(false);
    const [showNotSupportedOnMobile, setShowNotSupportedOnMobile] =
        React.useState(false);
    const [isSidebarExpanded, setIsSidebarExpanded] = React.useState(true);

    /**
     * Saves last used state of isSideBarExpanded
     */

    const saveExpandedStateToStorage = (isExpanded: boolean): void => {
        setValueToLocalStorage('isSideBarExpanded', {
            [user.id]: isExpanded
        });
    };

    /**
     * Checks local storage for "isSideBarExpanded" {[id]: boolean}.
     */
    const getInitialExpandedStateFromStorage = (): {
        [id: number]: boolean;
    } => getValueFromLocalStorage('isSideBarExpanded');

    const toggleSideBar = (toggle) => {
        setIsSidebarExpanded(toggle);
        saveExpandedStateToStorage(toggle);
    };
    const checkScreenSize = () => {
        // Checks if user is viewing on mobile device
        if (window.innerWidth < 1024) {
            if (props.history.location.pathname === '/') {
                setShowNotSupportedOnMobile(false);
            } else {
                setShowNotSupportedOnMobile(true);
            }
            setIsMobilePage(true);
            setIsSidebarExpanded(false);
        } else {
            setIsMobilePage(false);
            setShowNotSupportedOnMobile(false);
        }
    };
    React.useEffect(() => {
        const sidebarState = getInitialExpandedStateFromStorage();
        if (!sidebarState) {
            loadSession().then(({ data }) => {
                'lastSidebarExpanded' in data
                    ? setIsSidebarExpanded(data.lastSidebarExpanded)
                    : saveExpandedStateToStorage(true);
            });
        } else {
            setIsSidebarExpanded(sidebarState[user.id]);
        }
    }, []);
    React.useEffect(() => {
        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => {
            window.removeEventListener('resize', checkScreenSize);
        };
    }, []);

    React.useEffect(() => {
        checkScreenSize();
    }, [props.history.location.pathname]);
    const toggleShowNotSupported = (toggle) => {
        setShowNotSupportedOnMobile(toggle);
    };
    let project = null;

    const { firebaseAuth } = React.useContext(AuthContext);
    const [showIdleModal, setShowIdleModal] = React.useState(false);
    const onIdleTimeout = async () => {
        console.log('logout!');
        try {
            await axios.post(
                `${process.env.INCA_SERVER_URL}/auth/signout`,
                {},
                { withCredentials: true }
            );
            await firebaseAuth.signOut();
            localStorage.clear();
            window.location.href = process.env.AUTH_URL;
        } catch (e) {
            console.log(e);
        }
    };
    const onIdleOverride = () => {
        setShowIdleModal(false);
        idleTimer.activate();
        console.log('idle overrided!');
    };
    const onIdlePrompt = () => {
        setShowIdleModal(true);
    };
    const enableIdleTimeout = HARDENED_AUTH_TENANT_IDS.includes(getTenantId());
    const idleTimer = useIdleTimer({
        crossTab: true,
        onActive: onIdleOverride,
        onIdle: onIdleTimeout,
        onPrompt: onIdlePrompt,
        startOnMount: enableIdleTimeout,
        timeout: AUTH_CONFIG['IDLE_DELAY_BEFORE_PROMPT'],
        promptTimeout: AUTH_CONFIG['IDLE_DELAY_AFTER_PROMPT']
    });

    // React.useEffect(() => {
    //     if (
    //         props.currentProject?.status === PROJECT_STATUS.LIVE &&
    //         props.viewMode !== VIEW_MODE.DEV
    //     ) {
    //         setShowLiveProjectPopup(true);
    //     }
    // }, [props.currentProject?.status]);

    if (!_.isEmpty(props.currentProject)) project = props.currentProject;
    return (
        <div>
            <MiniModal isOpen={showIdleModal} onClose={onIdleOverride}>
                <MiniModalContent header={'Session Timeout'}>
                    <p>{`You will be logged out within the next minute unless you extend your session...`}</p>
                </MiniModalContent>
                <MiniModalActions>
                    <button onClick={onIdleOverride}>{'Extend Session'}</button>
                </MiniModalActions>
            </MiniModal>{' '}
            <div
                className={classnames('container-fluid', {
                    'fixed--margin': true
                })}
            >
                <SideBar
                    isFeedbackOpen={showFeedbackPopup}
                    isMobile={isMobilePage}
                    isSidebarExpand={isSidebarExpanded}
                    sidebarToggle={toggleSideBar}
                    toggleShowNotSupported={toggleShowNotSupported}
                    getInitialExpandedStateFromStorage={
                        getInitialExpandedStateFromStorage
                    }
                    openFeedback={() =>
                        setShowFeedbackPopup((current) => !current)
                    }
                />
                {showNotSupportedOnMobile ? (
                    <div className="row">
                        <PageNotSupportedOnMobile
                            toggleIsSupportedOnMobile={toggleShowNotSupported}
                        />
                    </div>
                ) : (
                    <>
                        {showFeedbackPopup && (
                            <FeedbackPopup
                                title="Leave us a message"
                                text="Got any comments, questions or concerns? Let us know and we will get back to you."
                                close={() => setShowFeedbackPopup(false)}
                            />
                        )}
                        <div
                            className="row"
                            onClick={() => {
                                if (isMobilePage && isSidebarExpanded) {
                                    toggleSideBar(false);
                                }
                            }}
                        >
                            <MainContainer
                                isMobile={isMobilePage}
                                isSidebarExpand={isSidebarExpanded}
                            >
                                {/* {showLiveProjectPopup && (
                                    <LiveProjectPopup
                                        show={showLiveProjectPopup}
                                        onClose={() => {
                                            setShowLiveProjectPopup(false);
                                            props.history.push('/');
                                        }}
                                        setEditEnabled={props.setEditEnabled}
                                    />
                                )} */}
                                {location.pathname.split('/')[1] !== '404' && (
                                    <div
                                        className="page-content-top"
                                        id="main-container-top"
                                    >
                                        <div className="page-content-left">
                                            <PageTitle
                                                pathname={location.pathname}
                                                project={project}
                                                viewMode={props.viewMode}
                                                location={props.location}
                                            />
                                        </div>
                                    </div>
                                )}

                                <WorkspaceRouter />
                            </MainContainer>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const mapStateToProps = (store: IApplicationState) => {
    const {
        projectReducer: { isNewProjectCreated, currentProject },
        utilsReducer: { viewMode },
        router: { location }
    } = store;
    return {
        viewMode,
        currentProject,
        isNewProjectCreated,
        location
    };
};
const mapDispatchToProps = (dispatch) => ({
    setEditEnabled: (enabled: boolean) => {
        dispatch(setEditEnabled(enabled));
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(Workspace);
